'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { Search, Palette, Wrench, Sparkles, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';
import { useRTL } from '@/hooks/useRTL';

gsap.registerPlugin(ScrollTrigger, MotionPathPlugin);

export default function TreatmentJourneySection() {
    const { t } = useTranslation('dental_veneers');
    const { isRTL } = useRTL();
    
    const journeySteps = [
        {
            step: 1,
            icon: Search,
            title: t('journey.steps.consultation.title'),
            description: t('journey.steps.consultation.description'),
            duration: t('journey.steps.consultation.duration'),
            details: t('journey.steps.consultation.details', { returnObjects: true }) as string[]
    },
    {
        step: 2,
        icon: Palette,
        title: t('journey.steps.preparation.title'),
        description: t('journey.steps.preparation.description'),
        duration: t('journey.steps.preparation.duration'),
        details: t('journey.steps.preparation.details', { returnObjects: true }) as string[]
    },
    {
        step: 3,
        icon: Wrench,
        title: t('journey.steps.fabrication.title'),
        description: t('journey.steps.fabrication.description'),
        duration: t('journey.steps.fabrication.duration'),
        details: t('journey.steps.fabrication.details', { returnObjects: true }) as string[]
    },
    {
        step: 4,
        icon: Sparkles,
        title: t('journey.steps.bonding.title'),
        description: t('journey.steps.bonding.description'),
        duration: t('journey.steps.bonding.duration'),
        details: t('journey.steps.bonding.details', { returnObjects: true }) as string[]
    },
    {
        step: 5,
        icon: Clock,
        title: t('journey.steps.followUp.title'),
        description: t('journey.steps.followUp.description'),
        duration: t('journey.steps.followUp.duration'),
        details: t('journey.steps.followUp.details', { returnObjects: true }) as string[]
    }
];

    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const timelineRef = useRef<HTMLDivElement>(null);
    const progressLineRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Set initial states
            gsap.set(titleRef.current, { opacity: 0, y: 50 });

            if (timelineRef.current?.children) {
                gsap.set(timelineRef.current.children, { opacity: 0, x: -50, scale: 0.9 });
            }

            if (progressLineRef.current) {
                gsap.set(progressLineRef.current, { scaleY: 0 });
            }

            // Title animation with scroll trigger
            gsap.to(titleRef.current, {
                opacity: 1,
                y: 0,
                duration: 1,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: titleRef.current,
                    start: "top 80%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse",
                    scrub: false
                }
            });

            // Individual timeline step animations - each step appears based on its own scroll position
            if (timelineRef.current?.children) {
                Array.from(timelineRef.current.children).forEach((step, index) => {
                    gsap.to(step, {
                        opacity: 1,
                        x: 0,
                        scale: 1,
                        duration: 1,
                        ease: "power3.out",
                        scrollTrigger: {
                            trigger: step,
                            start: "top 85%", // Step becomes visible when its top hits 85% of viewport
                            end: "bottom 15%",
                            toggleActions: "play none none reverse",
                            scrub: false
                        }
                    });
                });
            }

            // Animated progress line
            if (progressLineRef.current) {
                gsap.to(progressLineRef.current, {
                    scaleY: 1,
                    duration: 2,
                    ease: "power2.out",
                    transformOrigin: "top",
                    scrollTrigger: {
                        trigger: timelineRef.current,
                        start: "top 70%",
                        end: "bottom 30%",
                        toggleActions: "play none none reverse",
                        scrub: false
                    }
                });
            }

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="journey"
            ref={sectionRef}
            className="py-20 bg-slate-50 dark:bg-navy-950 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif"
                    >
                        {t('journey.title')}
                    </h2>
                    <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed">
                        {t('journey.subtitle')}
                    </p>
                </div>

                {/* Timeline */}
                <div className="relative max-w-4xl mx-auto">
                    {/* Progress Line */}
                    <div className="absolute left-8 md:left-12 top-0 bottom-0 w-1 bg-slate-200 dark:bg-slate-700 rounded-full">
                        <div
                            ref={progressLineRef}
                            className="w-full bg-gold rounded-full"
                        ></div>
                    </div>

                    {/* Timeline Steps */}
                    <div ref={timelineRef} className="space-y-12">
                        {journeySteps.map((step, index) => (
                            <motion.div
                                key={index}
                                className="relative flex items-start gap-8"
                                whileHover={{ x: 10 }}
                                transition={{ duration: 0.3 }}
                            >
                                {/* Step Number Circle */}
                                <div className="relative z-10 w-16 h-16 md:w-24 md:h-24 bg-gold rounded-full flex items-center justify-center shadow-xl">
                                    <div className="w-12 h-12 md:w-16 md:h-16 bg-white rounded-full flex items-center justify-center">
                                        <step.icon className="w-6 h-6 md:w-8 md:h-8 text-gold" />
                                    </div>

                                    {/* Step number badge */}
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-navy text-white rounded-full flex items-center justify-center text-sm font-bold">
                                        {step.step}
                                    </div>
                                </div>

                                {/* Content Card */}
                                <div className="flex-1 bg-white dark:bg-slate-800 rounded-2xl p-6 md:p-8 shadow-lg border border-slate-200 dark:border-slate-700 group hover:shadow-2xl transition-all duration-300">
                                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                                        <div className="flex-1">
                                            <h3 className="text-2xl md:text-3xl font-bold text-navy dark:text-white mb-3 group-hover:text-gold dark:group-hover:text-gold transition-colors duration-300">
                                                {step.title}
                                            </h3>
                                            <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed">
                                                {step.description}
                                            </p>
                                        </div>

                                        {/* Duration Badge */}
                                        <div className={cn(
                                            "inline-flex items-center gap-2 bg-gold/10 text-gold dark:text-gold rounded-full border border-gold/20 px-3 py-1.5 shrink-0 self-start",
                                            isRTL && "flex-row-reverse"
                                        )}>
                                            <Clock className="w-4 h-4 shrink-0" aria-hidden="true" />
                                            <span className={cn(
                                                "text-sm font-medium leading-none",
                                                isRTL && "font-arabic"
                                            )}>{step.duration}</span>
                                        </div>
                                    </div>

                                    {/* Details Grid */}
                                    <div className="grid sm:grid-cols-2 gap-3">
                                        {(Array.isArray(step.details) ? step.details : []).map((detail, detailIndex) => (
                                            <div
                                                key={detailIndex}
                                                className={cn(
                                                    "relative text-slate-600 dark:text-slate-300",
                                                    isRTL ? "pr-6 text-right" : "pl-6"
                                                )}
                                            >
                                                <div className={cn(
                                                    "w-2 h-2 bg-gold rounded-full absolute top-2",
                                                    isRTL ? "right-0" : "left-0"
                                                )}></div>
                                                <span className={cn(isRTL && "font-arabic")}>{detail}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Hover effect border */}
                                    <div className="absolute inset-0 rounded-2xl border-2 border-gold opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Duration Note */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    viewport={{ once: true }}
                    className="text-center mt-16"
                >
                    <div className="bg-gradient-to-r from-gold/10 to-gold/10 rounded-2xl p-8 border border-gold/20 max-w-3xl mx-auto">
                        <div className={cn(
                            "relative text-center mb-4",
                            isRTL ? "pr-12" : "pl-12"
                        )}>
                            <Clock className={cn(
                                "w-8 h-8 text-gold dark:text-gold absolute top-0",
                                isRTL ? "right-0" : "left-0"
                            )} />
                            <h3 className={cn(
                                "text-2xl font-bold text-navy dark:text-white",
                                isRTL && "font-arabic text-right"
                            )}>
                                {t('journey.timeline.title')}
                            </h3>
                        </div>
                        <p className="text-slate-600 dark:text-slate-300 text-lg">
                            {t('journey.timeline.description')}
                        </p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
} 
